import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, X, Plus } from 'lucide-react';
import api from '../api';
import { TIPI } from '../utils/tipi';
import VociListino, { FREE_MAX_VOCI } from '../components/VociListino';
import usePiano from '../hooks/usePiano';
import PlanModal from '../components/PlanModal';
import './CreaServizio.css';

// Tetto foto Pro (i free hanno FREE_MAX_FOTO).
const MAX_FOTO_PRO = 5;
const FREE_MAX_FOTO = 1;

// Crea una scheda del fornitore (una per tipo). Il tipo puo' arrivare pre-selezionato
// via querystring (?tipo=...) dai pulsanti "Aggiungi" della dashboard.
// La location e i contatti vivono nel profilo (vedi /dashboard/profilo).
function CreaServizio() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tipoParam = searchParams.get('tipo');
  const tipoIniziale = TIPI.some((t) => t.value === tipoParam) ? tipoParam : 'location';
  const { isPro } = usePiano();
  const MAX_FOTO = isPro ? MAX_FOTO_PRO : FREE_MAX_FOTO;
  const [categorieSuggerite, setCategorieSuggerite] = useState([]);
  const [formData, setFormData] = useState({
    nome: '',
    tipo: tipoIniziale,
    descrizione: '',
    prezzo_min: '',
    prezzo_max: '',
    eta_minima: '',
    eta_massima: '',
    indoor_outdoor: 'indoor',
    categoria: '',
  });
  const [elenco, setElenco] = useState([]);
  const [nuovaVoce, setNuovaVoce] = useState('');
  const [voci, setVoci] = useState([]);
  const [foto, setFoto] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);

  useEffect(() => {
    const loadCategorie = async () => {
      try {
        const res = await api.get(`/categorie?tipo=${formData.tipo}`);
        setCategorieSuggerite(res.data.categorie || []);
      } catch (err) {
        setCategorieSuggerite([]);
      }
    };
    loadCategorie();
  }, [formData.tipo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const aggiungiVoce = () => {
    const v = nuovaVoce.trim();
    if (v && !elenco.includes(v)) {
      setElenco((prev) => [...prev, v]);
    }
    setNuovaVoce('');
  };

  const rimuoviVoce = (idx) => {
    setElenco((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    setFoto((prev) => {
      const spazio = MAX_FOTO - prev.length;
      if (spazio <= 0) {
        setError(`Puoi caricare al massimo ${MAX_FOTO} foto per scheda.`);
        return prev;
      }
      if (files.length > spazio) {
        setError(`Puoi caricare al massimo ${MAX_FOTO} foto: ne sono state aggiunte solo ${spazio}.`);
      }
      const nuove = files.slice(0, spazio).map((file) => ({ file, preview: URL.createObjectURL(file) }));
      return [...prev, ...nuove];
    });
    e.target.value = '';
  };

  const removeFoto = (idx) => {
    setFoto((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!formData.nome.trim()) throw new Error('Il nome della scheda è obbligatorio');
      if (!formData.descrizione.trim()) throw new Error('La descrizione è obbligatoria');

      const payload = {
        tipo: formData.tipo,
        nome: formData.nome,
        descrizione: formData.descrizione,
        eta_minima: formData.eta_minima || null,
        eta_massima: formData.eta_massima || null,
        indoor_outdoor: formData.indoor_outdoor,
        categoria: formData.categoria || null,
        elenco_attivita: elenco,
        voci,
      };

      const res = await api.post('/servizi', payload);
      const schedaId = res.data.id;

      // Upload foto (endpoint separato) dopo la creazione della scheda.
      if (foto.length > 0 && schedaId) {
        const fd = new FormData();
        foto.forEach((f) => fd.append('files', f.file));
        await api.post(`/upload/servizio/${schedaId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      navigate('/dashboard', { state: { success: 'Scheda inviata! Sarà visibile online dopo l\'approvazione di un amministratore.' } });
    } catch (err) {
      setError(err.response?.data?.errore || err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="crea-servizio-page">
      <div className="form-container">
        <h1>Crea la tua scheda</h1>
        <p className="form-hint">
          La località e i contatti si impostano nel{' '}
          <a href="/dashboard/profilo">tuo profilo</a>.
        </p>

        <form onSubmit={handleSubmit} className="servizio-form">
          {error && <div className="error-message">{error}</div>}

          <fieldset>
            <legend>Informazioni di Base</legend>

            <div className="form-group">
              <label htmlFor="nome">Nome della scheda *</label>
              <input
                type="text" id="nome" name="nome"
                value={formData.nome} onChange={handleChange}
                placeholder="Es. Sala Feste Aurora" required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="tipo">Tipo *</label>
                <select id="tipo" name="tipo" value={formData.tipo} onChange={handleChange} required>
                  {TIPI.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="categoria">Sottocategoria</label>
                <input
                  type="text" id="categoria" name="categoria" list="categorie-list"
                  value={formData.categoria} onChange={handleChange}
                  placeholder="Es. sala interna, mago, gonfiabili..."
                />
                <datalist id="categorie-list">
                  {categorieSuggerite.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="descrizione">Descrizione * (no link/email)</label>
              <textarea
                id="descrizione" name="descrizione"
                value={formData.descrizione} onChange={handleChange}
                placeholder="Descrivi la tua attività..." rows="6" required
              />
            </div>
          </fieldset>

          <fieldset>
            <legend>Listino — pacchetti e prezzi</legend>
            <VociListino
              value={voci} onChange={setVoci}
              maxVoci={isPro ? Infinity : FREE_MAX_VOCI}
              onUpgrade={() => setPlanOpen(true)}
            />
          </fieldset>

          <fieldset>
            <legend>Cosa offri</legend>
            <div className="form-group">
              <label>Elenco delle cose che fai</label>
              <div className="elenco-input-row">
                <input
                  type="text" value={nuovaVoce}
                  onChange={(e) => setNuovaVoce(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aggiungiVoce(); } }}
                  placeholder="Es. Trucca bimbi"
                />
                <button type="button" className="btn btn-secondary" onClick={aggiungiVoce}>
                  <Plus size={18} /> Aggiungi
                </button>
              </div>
              {elenco.length > 0 && (
                <div className="chips">
                  {elenco.map((v, idx) => (
                    <span key={idx} className="chip">
                      {v}
                      <button type="button" onClick={() => rimuoviVoce(idx)}><X size={14} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </fieldset>

          <fieldset>
            <legend>Dettagli</legend>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="eta_minima">Età minima</label>
                <input type="number" id="eta_minima" name="eta_minima" min="0" max="18"
                  value={formData.eta_minima} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="eta_massima">Età massima</label>
                <input type="number" id="eta_massima" name="eta_massima" min="0" max="18"
                  value={formData.eta_massima} onChange={handleChange} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="indoor_outdoor">Ubicazione</label>
              <select id="indoor_outdoor" name="indoor_outdoor"
                value={formData.indoor_outdoor} onChange={handleChange}>
                <option value="indoor">Al chiuso</option>
                <option value="outdoor">All'aperto</option>
                <option value="entrambi">Interno + esterno</option>
              </select>
            </div>
          </fieldset>

          <fieldset>
            <legend>Foto (max {MAX_FOTO})</legend>
            {!isPro && (
              <p className="form-hint">
                Il piano gratuito permette {FREE_MAX_FOTO} foto.{' '}
                <button type="button" className="link-button" onClick={() => setPlanOpen(true)}>
                  Passa a Pro
                </button>{' '}
                per caricarne fino a {MAX_FOTO_PRO}.
              </p>
            )}
            {foto.length < MAX_FOTO ? (
              <div className="upload-area">
                <input type="file" id="foto" multiple accept="image/*" onChange={handleFotoChange} />
                <label htmlFor="foto" className="upload-label">
                  <Upload size={32} />
                  <p>Trascina le foto qui o clicca per selezionare</p>
                  <span>JPG, PNG fino a 5 MB · max {MAX_FOTO} foto</span>
                </label>
              </div>
            ) : (
              <p className="form-hint">Hai raggiunto il massimo di {MAX_FOTO} foto. Rimuovine una per aggiungerne altre.</p>
            )}
            {foto.length > 0 && (
              <div className="foto-preview">
                <h3>Foto selezionate ({foto.length}/{MAX_FOTO})</h3>
                <div className="preview-grid">
                  {foto.map((f, idx) => (
                    <div key={idx} className="preview-item">
                      <img src={f.preview} alt={`Preview ${idx}`} />
                      <button type="button" className="remove-btn" onClick={() => removeFoto(idx)}>
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </fieldset>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
              Annulla
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creazione in corso...' : 'Crea scheda'}
            </button>
          </div>
        </form>
      </div>
      <PlanModal open={planOpen} onClose={() => setPlanOpen(false)} />
    </div>
  );
}

export default CreaServizio;
